import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, TrendingUp, GitCompareArrows } from "lucide-react";
import { API, authHeaders } from "./shared";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const METRICS = [
  ["avg_base_rental_per_booking", "Avg Base Rental / Booking", true],
  ["avg_total_revenue_per_booking", "Avg Total Revenue / Booking", true],
  ["avg_revenue_per_rental_day", "Avg Revenue / Rental Day", true],
  ["avg_addon_revenue_per_booking", "Avg Add-On Revenue / Booking", true],
  ["avg_rental_length_days", "Avg Rental Length (days)", false],
  ["extension_revenue", "Extension Revenue", true],
  ["utilization_pct", "Vehicle Utilization %", false],
  ["idle_vehicle_days", "Idle Vehicle Days", false],
  ["revenue_per_available_day", "Revenue / Available Vehicle Day", true],
];

export default function PricingPerformanceModule({ token }) {
  const [d, setD] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });
  const [cmp, setCmp] = useState(null);
  const [cmpForm, setCmpForm] = useState({ prev_start: "", prev_end: "", new_start: "", new_end: "" });
  const [cmpBusy, setCmpBusy] = useState(false);

  const load = useCallback((r = {}) => {
    const params = {};
    if (r.start) params.start = r.start;
    if (r.end) params.end = r.end;
    axios.get(`${API}/pricing/performance`, { ...authHeaders(token), params })
      .then((res) => setD(res.data)).catch(() => setD({ error: true }));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const runCompare = async () => {
    const { prev_start, prev_end, new_start, new_end } = cmpForm;
    if (!prev_start || !prev_end || !new_start || !new_end) return;
    setCmpBusy(true);
    try {
      const { data } = await axios.get(`${API}/pricing/compare`, { ...authHeaders(token), params: cmpForm });
      setCmp(data);
    } catch { setCmp({ error: true }); }
    finally { setCmpBusy(false); }
  };

  if (!d) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading performance…</div>;
  if (d.error) return <p className="text-white/40 text-sm py-10 text-center">Could not load pricing performance.</p>;

  const dateCls = "bg-[#0C0C0C] border border-white/15 rounded px-2 py-1.5 text-xs text-white [color-scheme:dark]";

  return (
    <div data-testid="pricing-performance" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-white flex items-center gap-2"><TrendingUp size={17} className="text-[#F2B705]" /> Pricing Performance</h3>
          <p className="text-white/45 text-sm mt-0.5">Is the package strategy making more money? Window: {d.start} → {d.end}</p>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="perf-start" type="date" className={dateCls} value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} />
          <input data-testid="perf-end" type="date" className={dateCls} value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} />
          <button data-testid="perf-apply" onClick={() => load(range)} className="px-3 py-1.5 rounded-md bg-[#F2B705] text-black font-bold text-xs">Apply</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Bookings", d.bookings], ["3-Day Packages", d.three_day_package_bookings],
          ["Weekly Specials", d.weekly_special_bookings], ["Completed", d.completed_bookings],
          ["% Choosing All-In", `${d.pct_choosing_allin}%`], ["% Choosing Waiver", `${d.pct_choosing_damage_waiver}%`],
          ["Base Rental Revenue", money(d.base_rental_revenue)], ["Total Revenue", money(d.total_revenue)]].map(([l, v]) => (
          <div key={l} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
            <div className="text-white/45 text-[10px] uppercase tracking-widest">{l}</div>
            <div className="font-display font-black text-xl mt-1 text-white">{v}</div>
          </div>
        ))}
      </div>

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5 grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="perf-metrics">
        {METRICS.map(([k, l, isMoney]) => (
          <div key={k}>
            <div className="text-white/40 text-xs">{l}</div>
            <div className="text-[#F2B705] font-display font-black text-lg" data-testid={`perf-${k}`}>{isMoney ? money(d[k]) : d[k]}</div>
          </div>
        ))}
      </div>

      {(d.monthly_series || []).length > 0 && (
        <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5">
          <h4 className="text-white font-semibold text-sm mb-3">Monthly Revenue (base vs total)</h4>
          {d.monthly_series.map((m) => (
            <div key={m.month} className="flex items-center justify-between text-sm border-b border-white/5 py-1.5">
              <span className="text-white/60">{m.month} <span className="text-white/30 text-xs">({m.bookings} bookings)</span></span>
              <span className="text-white/80">Base {money(m.base)} · <span className="text-[#F2B705] font-semibold">Total {money(m.total)}</span></span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="pricing-compare">
        <h4 className="text-white font-semibold text-sm flex items-center gap-2 mb-1"><GitCompareArrows size={15} className="text-[#F2B705]" /> Old Pricing vs New Package Pricing</h4>
        <p className="text-white/40 text-xs mb-3">Pick the period before the package launch and the period after — see the % change for every metric.</p>
        <div className="flex flex-wrap items-end gap-2 mb-4">
          <div><div className="text-white/35 text-[10px] uppercase mb-1">Previous period</div>
            <div className="flex gap-2">
              <input data-testid="cmp-prev-start" type="date" className={dateCls} value={cmpForm.prev_start} onChange={(e) => setCmpForm((f) => ({ ...f, prev_start: e.target.value }))} />
              <input data-testid="cmp-prev-end" type="date" className={dateCls} value={cmpForm.prev_end} onChange={(e) => setCmpForm((f) => ({ ...f, prev_end: e.target.value }))} />
            </div></div>
          <div><div className="text-white/35 text-[10px] uppercase mb-1">New pricing period</div>
            <div className="flex gap-2">
              <input data-testid="cmp-new-start" type="date" className={dateCls} value={cmpForm.new_start} onChange={(e) => setCmpForm((f) => ({ ...f, new_start: e.target.value }))} />
              <input data-testid="cmp-new-end" type="date" className={dateCls} value={cmpForm.new_end} onChange={(e) => setCmpForm((f) => ({ ...f, new_end: e.target.value }))} />
            </div></div>
          <button data-testid="cmp-run" onClick={runCompare} disabled={cmpBusy}
            className="px-4 py-1.5 rounded-md bg-[#F2B705] text-black font-bold text-xs disabled:opacity-50">{cmpBusy ? "Comparing…" : "Run Report"}</button>
        </div>
        {cmp && !cmp.error && (
          <table className="w-full text-sm" data-testid="cmp-table">
            <thead><tr className="text-white/35 text-xs uppercase text-left">
              <th className="py-1.5">Metric</th><th className="text-right">Previous</th><th className="text-right">New</th><th className="text-right">Change</th></tr></thead>
            <tbody>
              {[["bookings", "Bookings", false], ["avg_base_rental_per_booking", "Avg Base Rental / Booking", true],
                ["avg_total_revenue_per_booking", "Avg Total Revenue / Booking", true], ["avg_revenue_per_rental_day", "Avg Revenue / Rental Day", true],
                ["avg_rental_length_days", "Avg Rental Length (days)", false], ["extension_revenue", "Extension Revenue", true],
                ["utilization_pct", "Utilization %", false], ["revenue_per_available_day", "Revenue / Available Day", true],
                ["base_rental_revenue", "Base Rental Revenue", true], ["total_revenue", "Total Revenue", true]].map(([k, l, isM]) => {
                const ch = cmp.pct_change?.[k];
                return (
                  <tr key={k} className="border-t border-white/5">
                    <td className="py-1.5 text-white/65">{l}</td>
                    <td className="text-right text-white/55">{isM ? money(cmp.previous[k]) : cmp.previous[k]}</td>
                    <td className="text-right text-white/85">{isM ? money(cmp.new[k]) : cmp.new[k]}</td>
                    <td className={`text-right font-semibold ${ch > 0 ? "text-emerald-400" : ch < 0 ? "text-red-400" : "text-white/40"}`}>
                      {ch === null || ch === undefined ? "—" : `${ch > 0 ? "+" : ""}${ch}%`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {cmp?.error && <p className="text-red-400/70 text-xs">Could not run the comparison.</p>}
      </div>
    </div>
  );
}
